/**
 * Sammelkennungen: Auswahl oder alle Personen dieser Verwaltung per E-Mail.
 * Der Einzelversand auf der Personenkarte bleibt daneben bestehen.
 */
import React, { useMemo, useState } from 'react';
import { Mail } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { matchesFreeText } from '@/components/search/SearchBuilder';

const TYPE_LABEL = {
  student: 'Schüler:in',
  teacher: 'Lehrkraft',
  guardian: 'Zahlungspflichtige:r',
};

export default function BulkAppKennungen({ personType, audienceLabel }) {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState([]);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const visible = useMemo(() => {
    return people.filter((person) => matchesFreeText(person, query));
  }, [people, query]);

  const openDialog = async () => {
    setResult(null);
    setQuery('');
    setPicked(new Set());
    setOpen(true);
    try {
      const res = await api.get('/admin/app-identities/recipients', { params: { person_type: personType } });
      setPeople(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setPeople([]);
      toast.error(formatApiError(err, 'Personen konnten nicht geladen werden'));
    }
  };

  const toggle = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectVisible = () => {
    setPicked((prev) => {
      const next = new Set(prev);
      visible.forEach((person) => {
        if (person.email) next.add(person.person_id);
      });
      return next;
    });
  };

  const send = async (personIds) => {
    setBusy(true);
    try {
      const res = await api.post('/admin/app-identities/bulk', {
        person_type: personType,
        person_ids: personIds,
      });
      setResult(res.data);
      const skipped = (res.data.skipped_no_email || []).length;
      const failed = (res.data.failed || []).length;
      toast.success(`${res.data.sent} Kennungen gesendet`);
      if (skipped || failed) {
        toast.message(`${skipped} ohne E-Mail, ${failed} fehlgeschlagen`);
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Versand fehlgeschlagen'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" onClick={openDialog} data-testid={`bulk-app-open-${personType}`}>
        <Mail className="w-4 h-4 mr-2" /> App-Kennungen senden
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-app-dialog">
          <DialogHeader>
            <DialogTitle>App-Kennungen senden</DialogTitle>
            <DialogDescription>
              Kennung und PIN gehen per E-Mail an {audienceLabel}. Wer schon eine offene Kennung hat, bekommt dieselbe noch einmal.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Namen suchen…"
            data-testid="bulk-app-search"
          />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{picked.size} ausgewählt</span>
            <button type="button" className="text-brand-red hover:underline" onClick={selectVisible} data-testid="bulk-app-select-visible">
              Angezeigte mit E-Mail auswählen
            </button>
          </div>
          <ul className="max-h-64 overflow-y-auto divide-y border border-slate-200 rounded-md">
            {visible.map((person) => (
              <li key={`${person.person_type}-${person.person_id}`} className="px-3 py-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    disabled={!person.email}
                    checked={picked.has(person.person_id)}
                    onChange={() => toggle(person.person_id)}
                    data-testid={`bulk-app-person-${person.person_id}`}
                  />
                  <span className="flex-1">
                    {person.name}
                    {personType === 'all' && (
                      <span className="ml-2 text-xs text-slate-400">{TYPE_LABEL[person.person_type]}</span>
                    )}
                  </span>
                  <span className="text-xs text-slate-400">{person.email || 'keine E-Mail'}</span>
                </label>
              </li>
            ))}
            {visible.length === 0 && <li className="px-3 py-6 text-center text-sm text-slate-400">Niemand in dieser Auswahl.</li>}
          </ul>
          {result && (
            <div className="text-sm text-slate-600" data-testid="bulk-app-result">
              <p>{result.sent} gesendet, {result.created} neu erzeugt, {result.reused} erneut geschickt.</p>
              {(result.skipped_no_email || []).length > 0 && (
                <p>Ohne E-Mail: {result.skipped_no_email.join(', ')}</p>
              )}
              {(result.failed || []).length > 0 && (
                <p>Fehlgeschlagen: {result.failed.join(', ')}</p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={busy || picked.size === 0}
              onClick={() => send([...picked])}
              data-testid="bulk-app-send-selected"
            >
              An Auswahl senden
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={() => send(null)}
              data-testid="bulk-app-send-all"
            >
              An alle {audienceLabel} senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
